import {
  type MutationFunctionContext,
  type QueryClient,
  queryOptions,
  useMutation,
} from '@tanstack/react-query';

import config from 'config';
import { getStorageLeadKeys, LEAD_STATE_STORAGE_KEY } from 'config/storage';
import type { Lead, LeadContext } from 'providers/LeadProvider';
import { resolveInitialLocalization } from 'providers/LocalizationProvider/utils';
import type { Country } from 'services/i18n';
import { experimentVariantManager } from 'utils/experimentation';
import { createAPIError } from 'utils/exceptions/apiError';

import type { OppSearchSignupResponse } from './opportunity';
import { readChallengeHash } from './otp';

/**
 * Deliberately narrow contract for create/update responses. The server may
 * return more of the Lead, but the client only consumes `id`, `status` and
 * `statusReason` — the lead query cache is the client-owned working copy and
 * is not overwritten from mutation responses (see ADR 013).
 */
export interface CreateLeadResponse {
  selfSignUpId: NonNullable<LeadContext['selfSignUpId']>;
  lead: {
    id: NonNullable<Lead['id']>;
    status: Lead['status'];
    statusReason: Lead['statusReason'];
  };
}

export interface CreateBaseLeadResponse {
  selfSignUpId: NonNullable<LeadContext['selfSignUpId']>;
  lead: Omit<Lead, 'id'> & {
    id: NonNullable<Lead['id']>;
  };
  requireAuthentication: boolean;
}

export interface CreateBaseLeadVariables {
  step: number;
  stepName: string;
  stepVersion: string;
  isLastStep: false;
  lastStepSubmitted: number;
  lead: {
    businessEmail: NonNullable<Lead['businessEmail']>;
    country: NonNullable<Lead['country']>;
    currency: Lead['currency'];
    language: Lead['language'];
    utmTerm?: Lead['utmTerm'];
    utmCampaign?: Lead['utmCampaign'];
    utmContent?: Lead['utmContent'];
    utmMedium?: Lead['utmMedium'];
    utmSource?: Lead['utmSource'];
    ssuCoupon?: Lead['ssuCoupon'];
    experimentVariant?: Lead['experimentVariant'];
  };
  isOppEnabled?: boolean;
}
/**
 * What identifies the Lead working copy: the Lead id. The hash only grants
 * access to the server's Lead, so it is not part of the identity (ADR 024).
 */
export interface LeadQueryVariables {
  leadId: string;
}

export interface GetLeadVariables extends LeadQueryVariables {
  emailValidationId: string;
}

export interface CompleteLeadVariables extends LeadQueryVariables {
  step?: number;
  /** The email whose challenge holds the hash the request sends. */
  businessEmail: string;
}

/**
 * Sends a lead creation request to the new V1 signup endpoint.
 *
 * @async
 * @function postCreateLead
 * @param variables - The parameters used to create a lead, focusing on essential data such as email, country, and currency.
 * @returns A promise resolving to a structure containing the created lead information, its associated self-sign-up ID, and an authentication requirement flag.
 * @throws Will throw an error if the response is not OK.
 */
async function postCreateLead(
  variables: CreateBaseLeadVariables,
): Promise<CreateBaseLeadResponse | OppSearchSignupResponse> {
  const url = new URL(`${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/signup`);
  const { isOppEnabled, ...rest } = variables;

  const experimentVariant = experimentVariantManager.resolveForPayload(
    rest.lead.experimentVariant,
  );

  const payload = {
    ...rest,
    lead: {
      ...rest.lead,
      ...(experimentVariant ? { experimentVariant } : {}),
    },
  };

  if (isOppEnabled) {
    url.searchParams.append('searchOpp', 'true');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await createAPIError(response);
  }

  const result = await response.json();

  return result;
}

export function isOppRecoveryResponse(
  response: CreateBaseLeadResponse | OppSearchSignupResponse,
): response is OppSearchSignupResponse {
  return 'opportunity' in response;
}

/**
 * Provides a React Query mutation hook for creating a new lead using the V1 endpoint.
 *
 * @function useCreateLeadV1
 * @returns An object containing mutate and status properties from the React Query mutation API.
 */
export function useCreateLeadV1() {
  return useMutation({
    mutationFn: postCreateLead,
  });
}

export interface UpdateLeadVariables {
  step: number;
  stepVersion: string;
  stepName: string;
  isLastStep: boolean;
  lastStepSubmitted: number;
  selfSignUpId: NonNullable<LeadContext['selfSignUpId']>;
  lead: Lead;
}

/**
 * Updates an existing lead with new data using a PUT request.
 *
 * @async
 * @function updateLead
 * @param variables - An object containing the step number, flags controlling the form flow, and the updated lead data.
 * @returns A promise resolving to a structure containing the updated lead information along with its self-sign-up ID.
 * @throws Will throw an error if the response is not OK.
 */
async function updateLead(
  variables: UpdateLeadVariables,
): Promise<CreateLeadResponse> {
  const url = `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/signup/${variables.selfSignUpId}`;
  const experimentVariant = experimentVariantManager.resolveForPayload(
    variables.lead.experimentVariant,
  );
  const payload = {
    ...variables,
    lead: {
      ...variables.lead,
      ...(experimentVariant ? { experimentVariant } : {}),
    },
  };
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await createAPIError(response);
  }

  const result = await response.json();

  return result;
}

/**
 * Reads the hash of the `businessEmail` challenge when it runs, so it is a
 * `mutationFn` that takes the query client from its context.
 */
export async function completeLead(
  variables: CompleteLeadVariables,
  { client }: MutationFunctionContext,
): Promise<CreateLeadResponse> {
  const url = `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/signup/complete`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      businessEmail: variables.businessEmail,
      emailValidationId: readChallengeHash({
        queryClient: client,
        email: variables.businessEmail,
      }),
      step: variables.step,
      leadId: variables.leadId,
    }),
  });

  if (!response.ok) {
    throw await createAPIError(response);
  }

  const result = await response.json();
  experimentVariantManager.clear();

  return result;
}

/**
 * Provides a React Query mutation hook for updating an existing lead.
 *
 * Bare mutation — it writes nothing to the query cache, and the response is
 * consumed only for `id`/`status`/`statusReason` (see {@link CreateLeadResponse}).
 * Callers are responsible for the write-through: after `mutateAsync` resolves,
 * call `useLead().updateLead()` with the submitted payload (plus the response's
 * status fields) to commit it to the lead query cache. See ADR 013.
 *
 * @function useUpdateLead
 * @returns An object containing mutate and status properties from the React Query mutation API.
 */
export function useUpdateLead() {
  return useMutation({
    mutationFn: updateLead,
  });
}

export interface GetLeadResponse {
  step: number;
  stepVersion: string;
  stepName: string;
  lastStepSubmitted: number;
  selfSignUpId: string | undefined;
  lead: Lead;
}

/**
 * Retrieves an existing lead's data under a confirmed hash through a GET request.
 * Lead recovery is its only caller (see ADR 013).
 *
 * @async
 * @function getLead
 * @param variables - The Lead id and the hash the BFF checks for the Lead's business email.
 * @returns The fetched lead data.
 * @throws Will throw an error when the response is not OK, such as the 401 for a hash the BFF does not accept.
 */
export async function getLead(
  variables: GetLeadVariables,
): Promise<GetLeadResponse> {
  const url = new URL(
    `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/signup/${variables.leadId}`,
  );
  url.searchParams.append('emailValidationId', variables.emailValidationId);

  const response = await fetch(url);

  if (!response.ok) {
    throw await createAPIError(response);
  }

  const result = await response.json();

  return result;
}

/**
 * Resolves the lead query's identity (leadId) from the query-cache identity
 * entry, allowing an explicit override.
 *
 * Reads via `getQueryData` — imperative, not a subscription. Callers only see
 * a new identity when something re-renders/re-runs them after the identity
 * entry changes.
 *
 * @function resolveLeadQueryVariables
 * @param queryClient - The query client holding the identity entry.
 * @param options - Optional override for leadId (e.g. from route context).
 * @returns An object containing the resolved leadId.
 */
export function resolveLeadQueryVariables(
  queryClient: QueryClient,
  options?: {
    leadId?: string;
  },
): LeadQueryVariables {
  const { leadId } = getStorageLeadKeys(queryClient);

  return {
    leadId: options?.leadId ?? leadId,
  };
}

/**
 * Generates the query key of the Lead working copy.
 *
 * @param variables - The Lead id.
 * @returns A tuple containing the lead state storage key and the Lead id.
 */
export function getLeadQueryKey({ leadId }: LeadQueryVariables) {
  return [LEAD_STATE_STORAGE_KEY, { leadId }] as const;
}

/**
 * Reads the lead query cache to retrieve stored lead data.
 *
 * @function readLeadQueryCache
 * @param queryClient - A React Query client instance for cache management.
 * @param variables - The parameters identifying a specific lead query.
 * @returns The cached lead data, or undefined if no matching data is found.
 */
export function readLeadQueryCache(
  queryClient: QueryClient,
  variables: LeadQueryVariables,
) {
  return queryClient.getQueryData<GetLeadResponse>(getLeadQueryKey(variables));
}

/**
 * Transforms raw or partial lead data by resolving localization defaults and
 * filling step/stepVersion/stepName fallbacks.
 *
 * @function transformLeadData
 * @param data - The fetched or locally stored lead response data.
 * @returns A GetLeadResponse with localization and step defaults applied.
 */
export function transformLeadData(
  data: GetLeadResponse | undefined,
): GetLeadResponse {
  const { currency, country } = resolveInitialLocalization(
    data?.lead?.country?.toLowerCase() as Country,
  );

  const lastStepSubmitted = data?.lastStepSubmitted ?? 1;

  return {
    step: data?.step ?? 1,
    stepVersion: data?.stepVersion ?? 'v0',
    stepName: data?.stepName ?? 'empty',
    selfSignUpId: data?.selfSignUpId,
    lastStepSubmitted,
    lead: {
      ...data?.lead,
      country,
      currency: currency?.iso,
    },
  };
}

/**
 * Query options for the lead query — the client-owned working copy of the
 * Lead (see ADR 013). A client store that never fetches: the cache is only
 * ever written imperatively (`useLead().updateLead`, loaders, Lead recovery in
 * the signup `onVerified`).
 *
 * `staleTime: 'static'` means the query function never runs again once the
 * entry has data, not after a stale time, not on invalidation
 * (https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults).
 * So it runs only when there is no working copy, and then the answer is the
 * empty Lead. A rerun that returned the empty Lead would wipe the working copy.
 *
 * @param variables - The lead query identity (leadId).
 * @returns The query configuration options for the lead query.
 */
export function getLeadQueryOptions(variables: LeadQueryVariables) {
  return queryOptions({
    queryKey: getLeadQueryKey(variables),
    queryFn: (): GetLeadResponse => ({
      step: 1,
      stepName: '',
      stepVersion: '',
      lastStepSubmitted: 1,
      selfSignUpId: '',
      lead: {},
    }),
    select: transformLeadData,
    staleTime: 'static',
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export const useCompleteLead = () => {
  return useMutation({
    mutationFn: completeLead,
  });
};
